<template>
  <div
    class="list-image-uploader border d-flex w-100 align-items-stretch h-100 position-relative flex-column hover-cursor"
    :key="inventory.uuid">
    <div @dragenter="onDragEnter" @dragleave="onDragLeave" @dragover.prevent @drop="onDrop"
         :class="{ dragging: isDragging}" @click="showImageGallery(inventory)"
         class="draggable-area w-100 d-flex align-items-center justify-content-center">
      <div style="width: 100%; height: 100%; position: absolute" @dragleave.prevent.stop="isDragging = false"></div>
      <img class="thumbnail" v-if="src" style="z-index: 4"
           :src="src" />
      <img class="thumbnail temp-thumbnail" v-else-if="items.length > 0"
           :src="temporaryInventoryMode ? items[0].base64 : items[0].base64_src"/>
      <div class="drag-here-text text-muted text-center" v-else>
        {{ $t('Drag image(s) here') }}
      </div>
      <div class="drop-here-text text-muted d-none text-center">
        {{ $t('Drop image(s) here') }}
      </div>
    </div>
    <b-progress class="mb-1 mt-1 mr-1 ml-1" animated striped variant="primary" v-if="uploadingProgress">
      <b-progress-bar :value="uploadingProgressValue" :label="`${uploadingProgressValue}%`"/>
    </b-progress>
    <image-viewer ref="imageViewer"/>
  </div>
</template>

<script>
  import ImageViewer from "../../core/components/image-viewer/ImageViewer";
  import InventoryService from "./InventoryService";
  import {Utils} from "@/core/services/utils.service";
  import {createNamespacedHelpers} from "vuex";
  import TemporaryItemsService from "@/modules/items/temporary/TemporaryItemsService";
  import {convertToHeic} from "../../core/helpers";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

  export default {
    name: "ListImageUploader",
    components: {ImageViewer},
    props: {
      inventory: {
        type: Object,
        required: true
      },
      temporaryInventoryMode: {
        type: Boolean,
        required: false
      }
    },
    data() {
      return {
        isDragging: false,
        dragCount: 0,
        items: [],
        uploadingProgressValue: 0,
        uploadingProgress: false,
      }
    },
    computed: {
      ...mapStateGlobal({
        imageFileFormats: state => state.imageFileFormats,
        imageMaxFileSize: state => state.imageMaxFileSize,
        videoFileFormats: state => state.videoFileFormats,
        videoMaxFileSize: state => state.videoMaxFileSize,
        maxImageCountPerLot: state => state.maxImageCountPerLot,
      }),
      src() {
        if (!this.inventory.images) {
          return null;
        }

        // Returns first image of the array
        for (let img of this.inventory.images) {
          if (this.temporaryInventoryMode && img.base64) {
            return img.base64;
          } else if (img.data && (img.data.thumb || img.data.original)) {
            return img.data.thumb ? img.data.thumb.url : img.data.original.url;
          }
        }
        return null;
      },
    },
    methods: {
      showImageGallery(item) {
        if (!this.src) {
          return;
        }
        this.$refs.imageViewer.show({
          items: item.images,
          title: item.short_description
        });
      },
      onDragEnter($event) {
        $event.preventDefault();
        this.isDragging = true;
        this.dragCount++;
      },
      onDragLeave($event) {
        $event.preventDefault();
        this.dragCount--;
        if (this.dragCount <= 0) {
          this.isDragging = false;
          this.dragCount = 0;
        }
      },
      async onDrop($event) {
        this.items = [];
        const filesArr = Array.from($event.dataTransfer.files);
        $event.preventDefault();
        $event.stopPropagation();
        this.isDragging = false;

        if (this.inventory.images.length + filesArr.length > this.maxImageCountPerLot) {
          this.$errorDialog(
              this.$i18n.t("Item can not contain more than {count} image(s).", {count: this.maxImageCountPerLot}),
              this.$i18n.t('Image Validation Error')
          );
          return
        }

        const failedFilesData = [];
        const successfulFiles = [];

        for (let file of filesArr) {
          const sizeInMbs = file.size / 1000000; // Convert bytes to megabytes

          if (![...this.videoFileFormats, ...this.imageFileFormats].includes(file.type)) {
            failedFilesData.push({failure_reason: 'type', file});
            continue;
          }

          if ((file.type.includes('image/') && sizeInMbs > this.imageMaxFileSize) ||
              (file.type.includes('video/') && sizeInMbs > this.videoMaxFileSize)) {
            failedFilesData.push({failure_reason: 'size', file});
          } else {
            successfulFiles.push(file);
          }
        }

        if (failedFilesData.length > 0) {
          Utils.parseFailedImagesData(failedFilesData, this);
          return;
        }

        for (let file of successfulFiles) {
          const f = await convertToHeic(file)
          await this.prepareData(f);
        }

        const imgArr = [
          ...this.inventory.images,
          ...this.items
        ];

        if (this.temporaryInventoryMode) {
          this.inventory.images = imgArr;
        }

        this.uploadingProgress = true;

        const progressEvent = (total, finished) => {
          this.uploadingProgressValue = parseInt("" + Math.round((finished / total) * 100));
        }

        let success, images, body, errorsList;
        if (this.temporaryInventoryMode) {
          try {
            const res = await TemporaryItemsService.createRenameAndDeleteImages(this.inventory.uuid, imgArr, '/api/v1/inventories/temporary-images/', progressEvent)
            success = res.success
            body = res.body

            const imagesResponse = await TemporaryItemsService.getImages(this.inventory.uuid)
            images = imagesResponse.body
          } catch (err) {
            success = false;
            images = null;
            body = err.body
            errorsList = err.errorsList
          }

          // let response = await TemporaryItemsService.updateTemporaryItem(this.inventory.uuid, this.inventory, 'images', progressEvent);
          // [success, images] = [response.success, response.body]
          // body = images
        } else {
          try {
            const res = await InventoryService.createRenameAndDeleteImages(this.inventory.uuid, imgArr, '/api/v1/inventories/images/', progressEvent)
            success = res.success
            images = res.images
            body = res.body
          } catch (err) {
            success = false;
            images = null;
            body = err.body
            errorsList = err.errorsList
          }
        }

        this.uploadingProgress = false;
        this.uploadingProgressValue = 0;

        if (success) {
          this.$toast('success', 'Files were uploaded successfully');
          this.$emit('imagesUploaded', {images, uuid: this.inventory.uuid});
        } else {
          if (body instanceof Array) {
            const listImagesArr = [];
            body.filter(i => i.image).map(e => listImagesArr.push(this.$createElement('li', {}, e.image[0])))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listImagesArr))
          } else if (errorsList instanceof Array && errorsList.length > 0) {
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, errorsList))
          } else {
            this.$errorDialog(this.$i18n.t('Something went wrong when creating images'));
          }
          return false;
        }
        //If dropped images are deleted instantly, without page load, 'items' still have the images and
        //faded out images are shown in table. So clearing 'items' is needed.
        this.items = [];
      },
      prepareData(file) {
        return new Promise((resolve) => {
          let fileReader = new FileReader();
          fileReader.onload = async (e) => {
            let item;
            if (this.temporaryInventoryMode) {
              item = {
                file: file,
                base64: e.target.result,
                blobPromise: InventoryService.convertBase64ToBlob(e.target.result),
                uuid: null,
                to_be_deleted: false,
              };
            } else {
              item = {
                file: file,
                base64_src: e.target.result,
                blobPromise: InventoryService.convertBase64ToBlob(e.target.result),
                uuid: null,
                processing: false,
                to_be_deleted: false,
              };
            }
            this.items.push(item);
            resolve(true)
          };
          fileReader.readAsDataURL(file);
        });
      },
    }
  }
</script>

<style scoped lang="scss">
  .list-image-uploader {

    .draggable-area {
      height: 5em;
      min-width: 5em;

      &.dragging {
        background-color: rgba(227, 232, 238, 0.49);

        .thumbnail {
          display: none;
        }

        .drag-here-text {
          display: none;
        }

        .drop-here-text {
          display: block !important;
        }
      }

      .thumbnail {
        max-height: 5em;
        max-width: 15em;
      }

      .temp-thumbnail {
        opacity: 0.5;
      }
    }
  }
</style>
